import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    DialogActions,
    CircularProgress,
    TextField,
    Switch,
    FormControlLabel,
    MenuItem,
    Grid,
    InputAdornment,
    IconButton
} from "@material-ui/core";

import { Cached, FileCopy } from "@material-ui/icons";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { generateToken } from "../../utils/generateToken";
import copyToClipboard from "../../utils/copyToClipboard";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    btnWrapper: {
        position: "relative",
    },
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
}));

const Schema = Yup.object().shape({
    tenantId: Yup.number().required("Tenant obrigatório"),
    localeServerId: Yup.number().required("Servidor local obrigatório"),
    DbserverId: Yup.number().required("Servidor DB obrigatório"),
    dbName: Yup.string().min(2, "Muito curto").required("dbName obrigatório"),
    dbUser: Yup.string().required("Usuário DB obrigatório"),
    dbPassHash: Yup.string().required("Senha DB obrigatória"),
    backendUrl: Yup.string().url("URL inválida").required("Backend URL obrigatória"),
    frontendUrl: Yup.string().url("URL inválida").required("Frontend URL obrigatória"),
    port: Yup.number().required("Porta obrigatória"),
    proxyPort: Yup.number().required("Porta proxy obrigatória"),
    jwtSecret: Yup.string().required("JWT Secret obrigatório"),
    jwtRefreshSecret: Yup.string().required("JWT Refresh Secret obrigatório"),
    token: Yup.string().required("Token obrigatório"),
});

const TenantsServerModal = ({ open, onClose, tenantsServerId, apiname }) => {
    const classes = useStyles();

    const getInitialState = () => ({
        tenantId: "",
        localeServerId: "",
        DbserverId: "",
        dbName: `api-${apiname}`,
        dbUser: "",
        dbPassHash: "",
        jwtSecret: generateToken(),
        jwtRefreshSecret: generateToken(),
        backendUrl: `https://api-${apiname}.send4whats.com`,
        frontendUrl: `https://app.send4whats.com`,
        port: "",
        proxyPort: 443,
        waMode: "pl_multi",
        useChrome: true,
        token: generateToken(),
    });

    const [tenantsServer, setTenantsServer] = useState(getInitialState());
    const [tenants, setTenants] = useState([]);
    const [localeServers, setLocaleServers] = useState([]);
    const [dbServers, setDbServers] = useState([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const { data } = await api.get("/tenantsServers/simpleListTenantsServers");
                setTenants(data.tenants || []);
                setLocaleServers(data.localeServers || []);
                setDbServers(data.dbServers || []);
            } catch (err) {
                toastError(err);
            }
        };
        if (open) fetchOptions();
    }, [open]);

    useEffect(() => {
        const fetchRecord = async () => {
            if (!tenantsServerId) return;
            try {
                const { data } = await api.get(`/tenantsServers/${tenantsServerId}`);
                setTenantsServer(data);
            } catch (err) {
                toastError(err);
            }
        };
        fetchRecord();
    }, [tenantsServerId]);

    useEffect(() => {
        if (!tenantsServerId && apiname) {
            setTenantsServer(getInitialState());
        }
    }, [apiname]);

    const handleSave = async (values) => {
        try {
            if (tenantsServerId) {
                await api.put(`/tenantsServers/${tenantsServerId}`, values);
            } else {
                await api.post("/tenantsServers/", values);
            }
            toast.success(i18n.t("tenantModal.success"));
            handleClose();
        } catch (err) {
            toastError(err);
        }
    };

    const handleClose = () => {
        onClose();
        setTenantsServer(getInitialState());
    };

    const handleGenerateToken = (setFieldValue, name = "token") => {
        const newToken = generateToken();
        setFieldValue(name, newToken);
        toast.info(i18n.t("global.success"));
    };
    const handleCopyToken = async (token) => {
        await copyToClipboard(token);
    };

    return (
        <div className={classes.root}>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle>
                    {tenantsServerId
                        ? `Editar Servidor do Tenant`
                        : `Adicionar Servidor ao Tenant`}
                </DialogTitle>
                <Formik
                    initialValues={tenantsServer}
                    enableReinitialize={true}
                    validationSchema={Schema}
                    onSubmit={async (values, actions) => {
                        try {
                            await handleSave(values);
                        } finally {
                            actions.setSubmitting(false);
                        }
                    }}
                >
                    {({ values, touched, errors, isSubmitting, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <Grid container spacing={2}>
                                    {/* Tenant */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            select
                                            fullWidth
                                            label="Tenant"
                                            name="tenantId"
                                            error={touched.tenantId && Boolean(errors.tenantId)}
                                            helperText={touched.tenantId && errors.tenantId}
                                            variant="outlined"
                                            margin="dense"
                                        >
                                            {tenants.map((t) => (
                                                <MenuItem key={t.id} value={t.id}>
                                                    {t.nickname} — {t.name}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>

                                    {/* Servidor Local */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            select
                                            fullWidth
                                            label="Servidor Local"
                                            name="localeServerId"
                                            error={touched.localeServerId && Boolean(errors.localeServerId)}
                                            helperText={touched.localeServerId && errors.localeServerId}
                                            variant="outlined"
                                            margin="dense"
                                        >
                                            {localeServers.map((s) => (
                                                <MenuItem key={s.id} value={s.id}>
                                                    {s.name} — {s.address}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>

                                    {/* Servidor DB */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            select
                                            fullWidth
                                            label="Servidor DB"
                                            name="DbserverId"
                                            error={touched.DbserverId && Boolean(errors.DbserverId)}
                                            helperText={touched.DbserverId && errors.DbserverId}
                                            variant="outlined"
                                            margin="dense"
                                        >
                                            {dbServers.map((s) => (
                                                <MenuItem key={s.id} value={s.id}>
                                                    {s.name} — {s.address}:{s.port}
                                                </MenuItem>
                                            ))}
                                        </Field>
                                    </Grid>

                                    {/* dbName */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="Nome do Banco (dbName)"
                                            name="dbName"
                                            error={touched.dbName && Boolean(errors.dbName)}
                                            helperText={touched.dbName && errors.dbName}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* dbUser */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="Usuário DB"
                                            name="dbUser"
                                            error={touched.dbUser && Boolean(errors.dbUser)}
                                            helperText={touched.dbUser && errors.dbUser}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* dbPassHash */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="Senha DB"
                                            name="dbPassHash"
                                            type="text"
                                            error={touched.dbPassHash && Boolean(errors.dbPassHash)}
                                            helperText={touched.dbPassHash && errors.dbPassHash}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* backendUrl */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="URL Backend"
                                            name="backendUrl"
                                            error={touched.backendUrl && Boolean(errors.backendUrl)}
                                            helperText={touched.backendUrl && errors.backendUrl}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* frontendUrl */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="URL Frontend"
                                            name="frontendUrl"
                                            error={touched.frontendUrl && Boolean(errors.frontendUrl)}
                                            helperText={touched.frontendUrl && errors.frontendUrl}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* port */}
                                    <Grid item xs={6} sm={3}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="Porta"
                                            name="port"
                                            type="number"
                                            error={touched.port && Boolean(errors.port)}
                                            helperText={touched.port && errors.port}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* proxyPort */}
                                    <Grid item xs={6} sm={3}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="Porta Proxy"
                                            name="proxyPort"
                                            type="number"
                                            error={touched.proxyPort && Boolean(errors.proxyPort)}
                                            helperText={touched.proxyPort && errors.proxyPort}
                                            variant="outlined"
                                            margin="dense"
                                        />
                                    </Grid>

                                    {/* waMode */}
                                    <Grid item xs={12} sm={3}>
                                        <Field
                                            as={TextField}
                                            select
                                            fullWidth
                                            label="Modo WA"
                                            name="waMode"
                                            variant="outlined"
                                            margin="dense"
                                        >
                                            <MenuItem value="pl_multi">pl_multi</MenuItem>
                                            <MenuItem value="single">single</MenuItem>
                                        </Field>
                                    </Grid>

                                    {/* useChrome */}
                                    <Grid item xs={12} sm={3} style={{ display: "flex", alignItems: "center" }}>
                                        <FormControlLabel
                                            control={
                                                <Field
                                                    as={Switch}
                                                    color="primary"
                                                    name="useChrome"
                                                    checked={values.useChrome}
                                                />
                                            }
                                            label="Usar Chrome"
                                        />
                                    </Grid>

                                    {/* jwtSecret */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="JWT Secret"
                                            name="jwtSecret"
                                            error={touched.jwtSecret && Boolean(errors.jwtSecret)}
                                            helperText={touched.jwtSecret && errors.jwtSecret}
                                            variant="outlined"
                                            margin="dense"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="generate token"
                                                            onClick={() => handleGenerateToken(setFieldValue, "jwtSecret")}
                                                        >
                                                            <Cached />
                                                        </IconButton>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="copy token"
                                                            onClick={() => handleCopyToken(values.jwtSecret)}
                                                        >
                                                            <FileCopy />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* jwtRefreshSecret */}
                                    <Grid item xs={12} sm={6}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="JWT Refresh Secret"
                                            name="jwtRefreshSecret"
                                            error={touched.jwtRefreshSecret && Boolean(errors.jwtRefreshSecret)}
                                            helperText={touched.jwtRefreshSecret && errors.jwtRefreshSecret}
                                            variant="outlined"
                                            margin="dense"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="generate token"
                                                            onClick={() => handleGenerateToken(setFieldValue, "jwtRefreshSecret")}
                                                        >
                                                            <Cached />
                                                        </IconButton>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="copy token"
                                                            onClick={() => handleCopyToken(values.jwtRefreshSecret)}
                                                        >
                                                            <FileCopy />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>

                                    {/* token */}
                                    <Grid item xs={12}>
                                        <Field
                                            as={TextField}
                                            fullWidth
                                            label="Token"
                                            name="token"
                                            error={touched.token && Boolean(errors.token)}
                                            helperText={touched.token && errors.token}
                                            variant="outlined"
                                            margin="dense"
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="generate token"
                                                            onClick={() => handleGenerateToken(setFieldValue, "token")}
                                                        >
                                                            <Cached />
                                                        </IconButton>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="copy token"
                                                            onClick={() => handleCopyToken(values.token)}
                                                        >
                                                            <FileCopy />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={handleClose}
                                    color="secondary"
                                    disabled={isSubmitting}
                                    variant="outlined"
                                >
                                    {i18n.t("global.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    color="primary"
                                    disabled={isSubmitting}
                                    variant="contained"
                                    className={classes.btnWrapper}
                                >
                                    {tenantsServerId
                                        ? i18n.t("global.buttons.okEdit")
                                        : i18n.t("global.buttons.okAdd")}
                                    {isSubmitting && (
                                        <CircularProgress size={24} className={classes.buttonProgress} />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default React.memo(TenantsServerModal);
